import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import * as FilePond from 'filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { Subscription } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { ConversationService } from 'src/app/services/conversation.service';

@Component({
    selector: 'app-upload-conversation-video',
    templateUrl: './upload-conversation-video.component.html',
    styleUrl: './upload-conversation-video.component.scss',
    standalone: false
})
export class UploadConversationVideoComponent implements OnInit {
	@ViewChild('myPond') myPond!: any;
	@Input() conversationID!: number;
	@Input() projectID!: number;
	@Output() startedUploading = new EventEmitter<boolean>();
	@Output() uploadComplete = new EventEmitter<void>();
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef;
	
	private uploadStatusSubscription!: Subscription; 
	private abortController: AbortController | null = null;

	pondOptions = {
    allowMultiple: false,
    acceptedFileTypes: ['video/mp4'],
		allowSyncAcceptAttribute: false,
		fileValidateTypeLabelExpectedTypes: 'Expects only video files!',
		labelFileTypeNotAllowed: 'Only MP4 files are allow.',
		labelIdle: 'Drag & Drop your files or <span class="filepond--label-action"> Browse </span><p class="m-0 fs-12 text-warning">** Support MP4 files only!</p>'
  };

	startTime!: number;
	videoSource!: string;
	canUpload = false;
	startUpload = false;
	submitted = false;

	currentTime = 0;
	progress = 0;
  timeLeft = '';
	speakers = [{ name: 'Single Speaker', value: 'y' }, { name: 'Multiple Speakers', value: 'n' }];
	speakerSelected: any = null;
	baseline_start_time: any = null;
	baseline_end_time: any = null;

	error_data = false;
	message_error = '';
	videoDuration: number = 0;

	constructor (private conversationService: ConversationService, private authService: AuthService) {}

	ngOnInit() {
    FilePond.registerPlugin(FilePondPluginFileValidateType);
    FilePond.create(this.myPond, this.pondOptions);
  }

	pondRemoveFile() {
		this.videoSource = '';
		if (this.videoElement) {
			this.videoElement.nativeElement.src = '';
		}
		this.speakerSelected = null;
		this.baseline_start_time = null;
		this.baseline_end_time = null;
		if (this.videoElement) {
			if (this.videoElement.nativeElement) {
				this.videoElement.nativeElement.removeAllListeners();
			}
		}
	}

	pondHandleAddFile(event: any) {
		if (event.error) {
			this.canUpload = false;
			if (event.file) {
				this.myPond.removeFile(event.file);
				this.authService.toastErrorMessage(event.error.main, event.error.sub);
			}
		} else {
			this.videoSource = URL.createObjectURL(event.file.file);
			this.canUpload = true;
			const interval = setInterval(() => {
				if (this.videoElement) {
					clearInterval(interval);
					this.setupVideoElement(this.videoSource);
				}
			}, 1000);
		}
	}

	setupVideoElement(videoURL: string): void {
		const video: HTMLVideoElement = this.videoElement.nativeElement;

		video.onloadedmetadata = () => {
			this.videoDuration = video.duration / 60;
		};

		video.addEventListener('timeupdate', () => {
			this.currentTime = Math.floor(video.currentTime);
		});

		video.src = videoURL;
	}

	markTime(label: string) {
		this.error_data = false;
		this.message_error = '';

		if (label === 'start'){
			this.baseline_start_time = this.currentTime;
			if (this.baseline_end_time) {
				if (this.baseline_start_time > this.baseline_end_time) {
					this.error_data = true;
					this.message_error = 'The baseline start time cannot be bigger than the baseline end time';
				}
			}
		} else {
			this.baseline_end_time = this.currentTime;
			if (this.baseline_start_time) {
				if (this.baseline_start_time > this.baseline_end_time) {
					this.error_data = true;
					this.message_error = 'The baseline start time cannot be bigger than the baseline end time';
				}
			}
		}
	}

	async uploadFile(){
		this.submitted = true;
		this.error_data = false;
		this.message_error = '';
		
		if (!this.speakerSelected) {
			this.error_data = true;
			this.message_error = 'You must choose number of speakers';
			return;
		}

		if (this.speakerSelected === 'n') {
			if (!this.baseline_start_time || !this.baseline_end_time) {
				this.error_data = true;
				this.message_error = 'You must mark the baseline start and end time';
				return;
			} else {
				if (this.baseline_start_time > this.baseline_end_time) {
					this.error_data = true;
					this.message_error = 'The baseline start time cannot be bigger than the baseline end time';
					return;
				}
			}
		}

		const data: any = {
			projectID: this.projectID,
			conversationID: this.conversationID,
			single_speaker: this.speakerSelected,
			video_length: this.videoDuration,
			baseline: {
				baseline_start: null,
				baseline_end: null,
				base_voice_start: null,
				base_voice_end: null
			}
		}

		if (this.speakerSelected === 'n') {
			data.baseline.base_voice_start = this.baseline_start_time;
			data.baseline.base_voice_end = this.baseline_end_time;
		}

		if (this.myPond.getFile()) {
			const filePondFile = this.myPond.getFile();
			const extension = this.getFileExtension(filePondFile.filename).toLowerCase();
			const fileName = 'conversation_' + this.conversationID + '.' + extension;
			const selectedFile: File = new File([filePondFile.file], fileName, { type: filePondFile.fileType });

			if (this.abortController) {
				this.abortController.abort();
			}

			this.abortController = new AbortController();
			this.startUpload = true;
			this.startedUploading.emit(true);

			this.uploadStatusSubscription = this.conversationService.getUploadStatusSubject().subscribe((a) => {
				if (a === 'abort' || a === 'error') {
					this.progress = 0;
					this.timeLeft = '';
					this.startUpload = false;
					this.startedUploading.emit(false);
					this.pondRemoveFile();

					if (this.uploadStatusSubscription) {
						this.uploadStatusSubscription.unsubscribe();
					}
				}

				if (a === 'complete') {
					data.status = 'ANALYZING';
					if (this.uploadStatusSubscription) {
						this.uploadStatusSubscription.unsubscribe();
					}

					this.conversationService.updateConversation(this.projectID, this.conversationID, data).subscribe({
						next: () => {
							this.uploadComplete.emit();
						},
						error: (err) => {
							this.authService.toastErrorMessage(err.message ? err.message : err, 'Updating Error');
							//TODO - what are we going to do if somthing went wrong on updating DB and sending to models... Error Handling here according to the error we get from the api call.
						}
					});
				}
			});

			await this.conversationService.uploadConversationFile(selectedFile, this.onProgress.bind(this), this.abortController.signal);
		}
  }

	cancelUpload() {
		if (this.abortController) {
			this.abortController.abort();
		}		
	}
	

	onProgress(loadedBytes: number, totalBytes: number) {
		// Calculate the progress percentage
		const progressPercentage = Math.floor((loadedBytes / totalBytes) * 100);
	
		// Update the progress bar or perform any other actions based on the progress
		this.progress = progressPercentage;
		this.timeLeft = this.calculateTimeLeft(loadedBytes, totalBytes);
	}

	calculateTimeLeft(loadedBytes: number, totalBytes: number): string {
		// Check if the upload is completed
		if (loadedBytes >= totalBytes) {
			return 'Done';
		}
	
		// Initialize the start time on the first progress update
		if (!this.startTime) {
			this.startTime = Date.now();
		}
	
		// Calculate the elapsed time since the start
		const elapsedTime = Date.now() - this.startTime;
	
		// Calculate the current speed (bytes per millisecond)
		const currentSpeed = loadedBytes / elapsedTime;
	
		// Estimate the time remaining (in milliseconds)
		const remainingTime = (totalBytes - loadedBytes) / currentSpeed;
	
		// Convert the remaining time to hours, minutes, and seconds
		const hours = Math.floor(remainingTime / (1000 * 60 * 60));
		const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
		const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
	
		// Build the time left string based on hours, minutes, and seconds
		let timeLeft = 'Time left: ';

		if (hours > 0) {
			timeLeft += hours + 'h ';
		}

		if (minutes > 0) {
			timeLeft += minutes + 'm ';
		}

		timeLeft += seconds + 's';
	
		return timeLeft;
	}

	getFileExtension(filename: string): string {
		const parts = filename.split('.');
		if (parts.length === 1) {
			return '';
		}

		return parts[parts.length - 1];
	}
}
